import React from 'react'
import { Alert, Button, Icon, Input, Upload } from 'antd'

import { starterPrompts } from '../../constants'
import {
  formatFileSize,
  getAcceptedAttribute,
  getReferenceKind,
  isStoppableBusyAction
} from '../../utils'
import './ConversationCard.scss'

const { TextArea } = Input

const ConversationCard = ({
  sessionId,
  messages,
  timelinePanel,
  scrollRef,
  errorMessage,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  isLockingVision,
  phaseBusyAction,
  acceptedExtensions,
  pendingReferences,
  activeWorkspace,
  draftMessage,
  onDraftMessageChange,
  onAttachReference,
  onRemovePendingReference,
  onSubmit,
  onStopAgentTurn,
  onKeyDown,
  onStarterPrompt,
  onSkipToShotList,
  isSkipDisabled
}) => {
  const canStopCurrentRequest =
    Boolean(onStopAgentTurn) &&
    (isStreaming ||
      isUploadingReference ||
      isStoppableBusyAction(phaseBusyAction))
  const isComposerBusy =
    !sessionId ||
    isStreaming ||
    isSynthesizingVision ||
    isCreatingSession ||
    isUploadingReference ||
    isLockingVision ||
    Boolean(phaseBusyAction)
  const hasPendingReferences = Boolean(pendingReferences.length)
  const shouldShowStarterPrompts = !messages.length

  const renderStarterPrompts = () => (
    <div className="creative-direction-chat__starter-panel creative-direction-chat__starter-panel--compact">
      <div className="creative-direction-chat__starter-panel-head">
        <span>Choose a starting prompt</span>
      </div>
      <div className="creative-direction-chat__starter-grid creative-direction-chat__starter-grid--compact">
        {starterPrompts.map(item => (
          <button
            type="button"
            className={`creative-direction-chat__starter-card creative-direction-chat__starter-card--compact${
              item.recommended
                ? ' creative-direction-chat__starter-card--recommended'
                : ''
            }`}
            key={item.key}
            aria-label={`${item.actionLabel}: ${item.title}`}
            disabled={isComposerBusy}
            onClick={() => onStarterPrompt(item)}
          >
            <span className="creative-direction-chat__starter-card-icon">
              <Icon type={item.icon} />
            </span>
            <span className="creative-direction-chat__starter-card-copy">
              <strong>{item.title}</strong>
              <small>{item.description}</small>
            </span>
            <span className="creative-direction-chat__starter-card-action">
              {item.actionLabel}
              <Icon type="arrow-right" />
            </span>
            {item.recommended ? (
              <em className="creative-direction-chat__starter-card-badge">
                Recommended
              </em>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <section className="creative-direction-chat__studio-card creative-direction-chat__conversation">
      <div className="creative-direction-chat__conversation-header">
        <div className="creative-direction-chat__conversation-title">
          <h2>Chat</h2>
          <p>Continue the conversation and review each completed step below.</p>
        </div>
        {onSkipToShotList ? (
          <div className="creative-direction-chat__skip-card creative-direction-chat__skip-card--in-chat">
            <div>
              <strong>Skip AI Director?</strong>
              <span>Go straight to Shot List and let AI build from your story.</span>
            </div>
            <Button
              icon="arrow-right"
              disabled={isSkipDisabled}
              onClick={onSkipToShotList}
            >
              Skip to Shot List
            </Button>
          </div>
        ) : null}
      </div>

      <div
        className={`creative-direction-chat__messages${
          !messages.length ? ' creative-direction-chat__messages--empty' : ''
        }`}
        ref={scrollRef}
      >
        {shouldShowStarterPrompts ? renderStarterPrompts() : null}

        {timelinePanel ? (
          <div className="creative-direction-chat__inline-workflow">
            {timelinePanel}
          </div>
        ) : null}
      </div>

      {errorMessage ? (
        <Alert
          className="creative-direction-chat__alert creative-direction-chat__alert--inline"
          type="error"
          showIcon
          message={errorMessage}
        />
      ) : null}

      <div
        className={`creative-direction-chat__interaction-dock${
          activeWorkspace && !shouldShowStarterPrompts
            ? ' creative-direction-chat__interaction-dock--with-phase'
            : ''
        }`}
      >
        {activeWorkspace && !shouldShowStarterPrompts ? (
          <div className="creative-direction-chat__dock-phase">
            {activeWorkspace}
          </div>
        ) : null}

        <div className="creative-direction-chat__composer">
          {hasPendingReferences ? (
            <div className="creative-direction-chat__pending-files">
              {pendingReferences.map(item => (
                <div
                  className="creative-direction-chat__pending-file"
                  key={item.id}
                >
                  <span className="creative-direction-chat__pending-preview">
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt="" />
                    ) : (
                      <Icon
                        type={
                          getReferenceKind(item.file) === 'video'
                            ? 'video-camera'
                            : 'picture'
                        }
                      />
                    )}
                  </span>
                  <span className="creative-direction-chat__pending-name">
                    {item.file.name}
                    {formatFileSize(item.file.size) ? (
                      <small>{formatFileSize(item.file.size)}</small>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${item.file.name}`}
                    disabled={isComposerBusy}
                    onClick={() => onRemovePendingReference(item.id)}
                  >
                    <Icon type="close" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="creative-direction-chat__composer-box">
            <div className="creative-direction-chat__upload-control">
              <Upload
                accept={getAcceptedAttribute(acceptedExtensions)}
                multiple
                showUploadList={false}
                beforeUpload={onAttachReference}
                disabled={isComposerBusy}
              >
                <button
                  type="button"
                  className="creative-direction-chat__attach-icon"
                  disabled={isComposerBusy}
                  aria-label="Attach image or video reference"
                  title="Attach image or video reference"
                >
                  <Icon type="paper-clip" />
                </button>
              </Upload>
            </div>
            <TextArea
              className="creative-direction-chat__composer-input"
              value={draftMessage}
              rows={1}
              disabled={isComposerBusy}
              aria-label="Message"
              placeholder="Type your answer or ask for a refinement..."
              onChange={event => onDraftMessageChange(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {canStopCurrentRequest ? (
              <Button
                className="creative-direction-chat__stop-button"
                icon="close-circle"
                onClick={onStopAgentTurn}
              />
            ) : (
              <Button
                type="primary"
                className="creative-direction-chat__send-button"
                loading={isUploadingReference}
                disabled={!draftMessage.trim() || isComposerBusy}
                onClick={onSubmit}
              >
                Send
                <Icon type="arrow-right" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ConversationCard
